import { LocationIcon as LocationSvg } from '@/assets/icons'
import { getInitials } from '@/utils/userNames'
import { PublicSectionGate } from '@components/PublicPage'
import { publicSectionPath } from '@components/TripOverview/core'
import { usePublicAccommodations, usePublicOverview } from '@hooks'
import { formatUtcInstant } from '@lib/datetime'
import { normalizeExternalUrl } from '@lib/utils'
import { GetPublicAccommodationsResponse, GetPublicOverviewResponse } from '@orbit/api-types'
import React from 'react'
import { useParams } from 'react-router-dom'
import { getGoogleMapsUrl } from '../Accommodations/accommodationHelpers'
import {
	DateBlock,
	DateBlockLabel,
	DateBlockTime,
	DateBlockValue,
	DatesSection,
	DetailBody,
	DetailCard,
	GuestAvatar,
	GuestItem,
	GuestList,
	GuestName,
	LinkItem,
	LinksList,
	LocationIcon,
	LocationPrimary,
	LocationSecondary,
	LocationSection,
	LocationText,
	MapButton,
	MetaSection,
	NotesContent,
	NotFoundMessage,
	SectionLabel,
} from '../Accommodations/AccommodationDetailPage/AccommodationDetailPage.styles'
import { buildTravelerLookup, travelerFullName } from './publicMembers'
import PublicSectionLayout from './PublicSectionLayout'

/** What the page renders from: the stays list plus the travelers that name guests. */
interface StayDetailData {
	detail: GetPublicAccommodationsResponse
	travelers: GetPublicOverviewResponse['travelers']
}

/**
 * Public, read-only accommodation detail at
 * `/shared/trips/:tripCode/accommodations/:accommodationId` — the member detail card
 * (location + map link, check-in/out, guests, links, notes) minus every edit
 * affordance and minus attachments (section attachments are not publicly
 * resolvable — ADR 0003). The stay resolves client-side from the already-cached
 * public stays list, exactly how the member page resolves from its list query;
 * guest names/avatars resolve from the overview's `travelers`. Both queries are
 * load-bearing, so the gate runs on their combined state, the stays error (the
 * deterministic 404 when the section is OFF) taking precedence.
 */
const PublicAccommodationDetailPage: React.FC = () => {
	const { tripCode, accommodationId } = useParams<{ tripCode: string; accommodationId: string }>()
	const staysQuery = usePublicAccommodations(tripCode)
	const overviewQuery = usePublicOverview(tripCode)

	const combined = {
		data:
			staysQuery.data && overviewQuery.data
				? ({ detail: staysQuery.data, travelers: overviewQuery.data.travelers } satisfies StayDetailData)
				: undefined,
		isLoading: staysQuery.isLoading || overviewQuery.isLoading,
		error: staysQuery.error ?? overviewQuery.error,
	}

	const listCrumb = { label: 'Accommodations', path: publicSectionPath(tripCode ?? '', 'accommodations') }

	return (
		<PublicSectionGate
			query={combined}
			loadingLabel="Loading accommodation…"
			unavailableTitle="Accommodation Unavailable"
			unavailableMessage="This accommodation doesn't exist or hasn't been shared publicly by the trip owner."
		>
			{({ detail, travelers }) => {
				const accommodation = detail.accommodations.find(
					a => String(a.id) === accommodationId,
				)
				const lookup = buildTravelerLookup(travelers)

				if (!accommodation) {
					return (
						<PublicSectionLayout
							tripCode={tripCode ?? ''}
							title="Accommodations"
							parentCrumb={listCrumb}
						>
							<NotFoundMessage>Accommodation not found</NotFoundMessage>
						</PublicSectionLayout>
					)
				}

				const mapsUrl = getGoogleMapsUrl(accommodation.location)
				const guests = accommodation.guests ?? []
				const links = accommodation.links ?? []
				const addedBy = travelerFullName(lookup(accommodation.createdByUserId))

				return (
					<PublicSectionLayout
						tripCode={tripCode ?? ''}
						title={accommodation.name}
						subtitle={accommodation.location?.locality || accommodation.location?.secondaryText || undefined}
						parentCrumb={listCrumb}
					>
						<DetailCard>
							<DetailBody>
								{accommodation.location && (
									<LocationSection>
										<LocationIcon>
											<LocationSvg sx={{ fontSize: 20 }} />
										</LocationIcon>
										<LocationText>
											<LocationPrimary>
												{accommodation.location.primaryText || accommodation.location.fullText}
											</LocationPrimary>
											{accommodation.location.secondaryText && (
												<LocationSecondary>{accommodation.location.secondaryText}</LocationSecondary>
											)}
										</LocationText>
										{mapsUrl && (
											<MapButton href={mapsUrl} target="_blank" rel="noopener noreferrer">
												View Map
											</MapButton>
										)}
									</LocationSection>
								)}

								<DatesSection>
									<DateBlock>
										<DateBlockLabel>Check-in</DateBlockLabel>
										<DateBlockValue>
											{formatUtcInstant(accommodation.checkInDate, 'dateWeekday')}
										</DateBlockValue>
										<DateBlockTime>{formatUtcInstant(accommodation.checkInDate, 'time')}</DateBlockTime>
									</DateBlock>
									<DateBlock $align="right">
										<DateBlockLabel>Check-out</DateBlockLabel>
										<DateBlockValue>
											{formatUtcInstant(accommodation.checkOutDate, 'dateWeekday')}
										</DateBlockValue>
										<DateBlockTime>{formatUtcInstant(accommodation.checkOutDate, 'time')}</DateBlockTime>
									</DateBlock>
								</DatesSection>

								<div>
									<SectionLabel>Guests ({guests.length})</SectionLabel>
									<GuestList>
										{guests.map(guest => {
											const traveler = lookup(guest.userId)
											if (!traveler) return null
											return (
												<GuestItem key={guest.userId}>
													<GuestAvatar>
														{traveler.profilePicture ? (
															<img src={traveler.profilePicture} alt={getInitials(traveler)} />
														) : (
															getInitials(traveler)
														)}
													</GuestAvatar>
													<GuestName>{travelerFullName(traveler)}</GuestName>
												</GuestItem>
											)
										})}
									</GuestList>
								</div>

								{links.length > 0 && (
									<div>
										<SectionLabel>Links ({links.length})</SectionLabel>
										<LinksList>
											{links.map((link, index) => (
												<LinkItem
													key={index}
													href={normalizeExternalUrl(link)}
													target="_blank"
													rel="noopener noreferrer"
												>
													{link}
												</LinkItem>
											))}
										</LinksList>
									</div>
								)}

								{accommodation.notes && (
									<div>
										<SectionLabel>Notes</SectionLabel>
										<NotesContent>{accommodation.notes}</NotesContent>
									</div>
								)}

								{addedBy && (
									<MetaSection>
										<span>Added by {addedBy}</span>
									</MetaSection>
								)}
							</DetailBody>
						</DetailCard>
					</PublicSectionLayout>
				)
			}}
		</PublicSectionGate>
	)
}

export default PublicAccommodationDetailPage
